import { useCallback, useState } from 'react';

import { getJobsBusinessUnitApiAddressUrl } from '~/config';
import { useBusinessUnitContext } from '~/contexts/BusinessUnitContext/BusinessUnitContext';
import { useCacheFromCallback, useDebouncedCallback } from '~/hooks';
import type { Prediction } from '~/utils/address';

type GetAutocompleteSuggestion = (input: string) => Promise<void>;

export enum AWS_LOCATION_RESULT_TYPE {
  CITY = 'CITY',
  ADDRESS = 'ADDRESS',
  COUNTRY = 'COUNTRY',
  STREET = 'STREET',
  ZIP = 'ZIP',
}

export const IN_COUNTRY_CATEGORY_TYPES = [
  AWS_LOCATION_RESULT_TYPE.CITY,
  AWS_LOCATION_RESULT_TYPE.ADDRESS,
  AWS_LOCATION_RESULT_TYPE.STREET,
  AWS_LOCATION_RESULT_TYPE.ZIP,
];

export const DEFAULT_CATEGORY_TYPE = AWS_LOCATION_RESULT_TYPE.CITY;

export type UsePredictionProps = { debounceDelay?: number; types?: AWS_LOCATION_RESULT_TYPE[] };
export type UsePredictionResult = [Prediction[] | null, boolean, GetAutocompleteSuggestion];

export const usePredictions = ({
  types = [AWS_LOCATION_RESULT_TYPE.ADDRESS],
  debounceDelay = 1000,
}: UsePredictionProps = {}): UsePredictionResult => {
  const { businessUnit } = useBusinessUnitContext();

  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState<Prediction[] | null>(null);

  const findPredictions = useCacheFromCallback(async input => {
    try {
      const response = await fetch(getJobsBusinessUnitApiAddressUrl(businessUnit, 'autocomplete'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input, types }),
      });
      const data = (await response.json()) as Prediction[] | null;
      if (data) return data;
    } catch (e) {
      console.error(e);
    }
    return [];
  });

  const [debouncePredictions, cancelDebouncePredictions] = useDebouncedCallback(async (input: string) => {
    setLoading(true);
    setPredictions(await findPredictions(input));
    setLoading(false);
  }, debounceDelay);

  const getPredictions = useCallback(async (input: string) => {
    if (input) {
      await debouncePredictions(input);
    } else {
      cancelDebouncePredictions();
      setPredictions([]);
    }
  }, []);

  return [predictions, loading, getPredictions];
};
